import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { FollowUpLinkType } from '@app/modules/follow-ups/follow-ups.types';
import { FollowUpsUtilsService } from '@app/modules/follow-ups/services/follow-ups-utils.service';
import { SampleListDialogComponent } from '@app/modules/service-data/components/sample/sample-list-dialog/sample-list-dialog.component';
import { SamplesListComponent } from '@app/modules/service-data/components/sample/samples-list/samples-list.component';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import {
  getDateOnly,
  getFieldSet,
  ValidatorsHandlerType,
} from '@app/modules/service-data/service-data.utils';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import { GetServiceDataAction } from '@app/modules/service-data/store/service-data.actions';
import { ServiceDataState } from '@app/modules/service-data/store/service-data.state';
import {
  ExtendedServiceData,
  PregnancyType,
  SharedServiceData,
} from '@app/modules/service-data/types/service-data.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { LanguageState } from '@core/store/languages/languages.state';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import {
  NgbActiveModal,
  NgbModal,
  NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { DisplayNamePipe } from '@shared/pipes/display-name.pipe';
import { combineLatest, Observable, of, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ServiceDataFinanceDetailsComponent } from '../service-data-finance-details/service-data-finance-details.component';

@Component({
  selector: 'app-service-data-details',
  templateUrl: './service-data-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslateModule,
    SamplesListComponent,
    DatePipe,
    AsyncPipe,
    NgbTooltip,
    RequirePermissionDirective,
    DisplayNamePipe,
  ],
})
export class ServiceDataDetailsComponent implements OnInit {
  protected coreUtilsService = inject(CoreUtilsService);
  protected serviceDataUtilsService = inject(ServiceDataUtilsService);
  protected dialogRef = inject(NgbActiveModal);
  private ngbModal = inject(NgbModal);
  private store = inject(Store);
  private authService = inject(AccountUtilsService);

  @Input()
  public serviceDataId: string;

  protected languages = this.store.selectSignal(LanguageState.getLanguages);
  protected canViewCommentsAndNotes = computed(
    () =>
      this.authService.hasRequiredPermission(
        'serviceData.details.patientState'
      ) || this.authService.hasRequiredPermission('serviceData.details.notes')
  );

  followUpsUtils = inject(FollowUpsUtilsService);

  protected extendedServiceData$: Observable<ExtendedServiceData>;
  protected sharedServiceData$: Observable<SharedServiceData>;
  protected fieldSet: string[] = [];

  ngOnInit() {
    this.extendedServiceData$ = this.store
      .dispatch(new GetServiceDataAction(this.serviceDataId))
      .pipe(
        switchMap(() => this.store.select(ServiceDataState.getServiceDataById)),
        map((getById) => getById(this.serviceDataId)),
        switchMap((serviceData) =>
          this.serviceDataUtilsService.getExtendedServiceData(serviceData, {
            partner: true,
            doctor: true,
            bundles: true,
            invoice: true,
          })
        )
      );

    this.extendedServiceData$
      .pipe(
        filter(Boolean),
        switchMap((serviceData) =>
          combineLatest([
            of(serviceData),
            this.store
              .select(ServiceDataState.getAvailableProducts)
              .pipe(
                map((getAvailableProducts) =>
                  getAvailableProducts(serviceData.id)
                )
              ),
          ])
        )
      )
      .subscribe(([serviceData, availableProducts]) => {
        const inputTypes = (availableProducts ?? [])
          .filter(Boolean)
          .map((product) => product.dataInputType);
        this.sharedServiceData$ = of(
          this.serviceDataUtilsService.serviceDataToForm(
            serviceData,
            inputTypes
          )
        );
        this.fieldSet = getFieldSet(
          inputTypes,
          ValidatorsHandlerType.SERVICEDATA
        );
      });
  }

  protected manageSamples(serviceDataId, sampleIds) {
    const modalRef = this.ngbModal.open(SampleListDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.serviceDataId = serviceDataId;
    modalRef.componentInstance.sampleIds = sampleIds;
  }

  protected editServiceData(serviceDataId: string) {
    this.serviceDataUtilsService.openEdit(serviceDataId);
  }

  protected openPaymentDetails(serviceDataId: string) {
    const modalRef = this.ngbModal.open(ServiceDataFinanceDetailsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.id = serviceDataId;
  }

  protected addFollowUp(serviceData: ExtendedServiceData) {
    this.followUpsUtils.openCreateFollowUps(
      [serviceData.id],
      FollowUpLinkType.ServiceData,
      [{ id: serviceData.referenceKey }]
    );
  }

  protected readonly PregnancyType = PregnancyType;
  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly getDateOnly = getDateOnly;
}
